import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { CSVLink } from 'react-csv';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../../shared/components/VerbButton';
import VerbText from '../../../../../../shared/components/VerbText';
import { CompanyFormMemberTable } from './CompanyFormMemberTable';
import SearchForm from '../../../../../../shared/containers/forms/SearchForm';
import { getCompanyMembers } from '../../../../../../shared/utils/FetchUtils/CompanyFetchUtils';
import FullPageLoadingIndicator from '../../../../../../shared/components/FullPageLoadingIndicator';
import { RuntimeContext } from '../../../../../../../common/RuntimeContext';

export default class RegisteredMembersTab extends Component {
    constructor(props) {
        super(props);
        this.csvLink = React.createRef();

        this.state = {
            membersToDelete: [],
            memberRemovedSuccess: '',
            csvData: [],
            loading: false
        };
    }

    static propTypes = {
        company: PropTypes.object,
        removeMembersFromCompany: PropTypes.func,
        updatingMembers: PropTypes.bool,
        registeredMembers: PropTypes.array,
        isVerbAdmin: PropTypes.bool,
        handleClearSearchBar: PropTypes.func,
        handleCompanyMemberSearch: PropTypes.func,
        searchHasEmptyResults: PropTypes.bool,
        totalMemberCount: PropTypes.number
    };

    static contextType = RuntimeContext;

    _selectToDelete = (user) =>
        this.setState((prevstate) => {
            const { membersToDelete } = prevstate;

            const indexOfMemberIfInList = membersToDelete.indexOf(user._id);
            if (indexOfMemberIfInList > -1) {
                membersToDelete.splice(indexOfMemberIfInList, 1);
            } else {
                membersToDelete.push(user._id);
            }
            return {
                membersToDelete
            };
        });

    _removeMembers = () => {
        const { membersToDelete } = this.state;
        const { removeMembersFromCompany } = this.props;
        return removeMembersFromCompany(membersToDelete).then(() =>
            this.setState({
                membersToDelete: [],
                memberRemovedSuccess: `${membersToDelete.length} members have been removed from the company.`
            })
        );
    };

    _formatDate = (date) =>
        new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    _generateCSVData = () => {
        const { company, totalMemberCount } = this.props;
        const LIMIT = 1000;

        this.setState({ loading: true });

        // split requests based on LIMIT
        const getMembersPromises = new Array(Math.ceil(totalMemberCount / LIMIT)).fill(0).map((_, index) =>
            getCompanyMembers({
                companyId: company._id,
                query: {
                    registeredOrder: 'desc',
                    page: index,
                    limit: LIMIT,
                    includeTeams: true,
                    includeContentCompletion: true
                },
                pathsContext: this.context.pathsWeb
            })
        );

        // responses = array of arrays [[], []]
        return Promise.all(getMembersPromises).then((responses) => {
            const registeredMembers = responses.flat(); // flatten array to be combined array of profiles

            // array of arrays [[], []]
            const users = registeredMembers.map(
                ({
                    firstName,
                    lastName,
                    email,
                    registeredDateTime: registeredDate,
                    lastSignInDateTime: lastSignInDate,
                    teams = [],
                    pathwaysCompleted,
                    collectionsCompleted,
                    activitiesCompleted
                }) => [
                    firstName,
                    lastName,
                    email,
                    this._formatDate(registeredDate),
                    this._formatDate(lastSignInDate),
                    teams.reduce((accum, team) => `${accum}, ${team.name}`, '').slice(1),
                    pathwaysCompleted,
                    collectionsCompleted,
                    activitiesCompleted
                ]
            );

            const csvData = [
                [
                    'First name',
                    'Last name',
                    'Email',
                    'Registered Date',
                    'Last Session Date',
                    'Teams',
                    'Pathways Completed',
                    'Collections Completed',
                    'Activities Completed'
                ],
                ...users
            ];
            this.setState({ csvData, loading: false }, () => {
                this.csvLink.current.link.click();
            });
        });
    };

    getMembersTable = () => {
        const { loading, membersToDelete } = this.state;
        const {
            registeredMembers,
            company,
            updatingMembers,
            isVerbAdmin = false,
            searchHasEmptyResults = false
        } = this.props;

        const hasMembers = searchHasEmptyResults || (registeredMembers && registeredMembers.length);

        if (!loading) {
            return (
                <>
                    {hasMembers ? (
                        <Fragment>
                            <div className={'col-12'}>
                                <small className={'no-results'}>
                                    {searchHasEmptyResults && (!registeredMembers || !registeredMembers.length)
                                        ? `Sorry!\nYour search didn't return any results.`
                                        : null}
                                </small>
                            </div>
                            <CompanyFormMemberTable
                                memberProfiles={registeredMembers}
                                isRegisteredMemberTable={true}
                                company={company}
                                isUpdatingMembers={updatingMembers}
                                handleClick={this._selectToDelete}
                                selectedList={membersToDelete}
                                selectedField={'_id'}
                                fontAwesomeIcon={'fa-trash'}
                                actionTitle={'Delete'}
                                dateTitle={'Registered'}
                                dateKey={'registeredDateTime'}
                                linkToEditProfiles={isVerbAdmin}
                            />
                        </Fragment>
                    ) : (
                        <div className={'col-sm-12'}>
                            <small>
                                <VerbText
                                    messageId={'COMPANY_NO_REGISTERED_MEMBERS'}
                                    defaultMessage={`This company does not have any registered members yet.`}
                                />
                            </small>
                        </div>
                    )}
                </>
            );
        }
        return null;
    };

    render() {
        const {
            registeredMembers,
            company,
            handleCompanyMemberSearch,
            handleClearSearchBar,
            searchHasEmptyResults = false
        } = this.props;
        const { membersToDelete, memberRemovedSuccess, loading, csvData } = this.state;
        const hasMembers = searchHasEmptyResults || (registeredMembers && registeredMembers.length);
        const csvFilename = !company ? 'Registerd_Members' : `${company.name} Registered_Members`;

        return (
            <div>
                <div className={'row'} style={{ margin: '0' }}>
                    <div className={'col-12 col-lg-8 form-action-buttons formButtons'}>
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.PRIMARY_DEFAULT}
                            disabled={loading}
                            onClick={this._generateCSVData}
                            textId={loading ? 'LOADING' : 'ACTION_EXPORT_CSV'}
                            defaultMessage={loading ? 'LOADING...' : 'EXPORT CSV'}
                            className={'form-button'}
                        />
                        <CSVLink ref={this.csvLink} className={'d-none'} data={csvData} filename={csvFilename} />
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                            onClick={this._removeMembers}
                            disabled={!membersToDelete.length}
                            textId={'ACTION_DELETE_MEMBERS'}
                            fontAwesomeIcon={'fal fa-trash'}
                            defaultMessage={'DELETE MEMBERS'}
                            className={'form-button'}
                        />
                    </div>
                    {!!hasMembers && (
                        <div className={'col-12 col-lg-4 ms-auto company-member-search'}>
                            <SearchForm
                                indexName={'Profile'}
                                showBottomBorder={false}
                                showFullBorder={true}
                                onClearSearch={handleClearSearchBar}
                                handleSearchResults={handleCompanyMemberSearch}
                                onSubmit={handleCompanyMemberSearch}
                                defaultFilters={`(companyId: ${company._id})`}
                                pathsContext={this.context.pathsWeb}
                            />
                        </div>
                    )}
                </div>
                {!!hasMembers && (
                    <div className={'row col-6 me-auto margin'}>
                        <div className={'row success-message me-auto'}>{memberRemovedSuccess}</div>
                    </div>
                )}
                {loading && (
                    <div className={'col-12 registered-members-loading-indicator'}>
                        <FullPageLoadingIndicator />
                    </div>
                )}
                {this.getMembersTable()}
            </div>
        );
    }
}
